import {
  HomeWorkoutSequence, ISessionSequenceInfo, Phase, UserProfile, UserWorkoutSession,
} from 'fitbeat.gym-core';
import { createSelector } from 'reselect';
import theme from '../../theme';
import { NUMBER_OF_SEQUENCES_TO_PRELOAD } from '../actions';
import { IGroupParticipants, MusicInitialiseStates } from '../components';
import { IAppStore, Orientation, Orientations } from '../reducer';

export const hasSetupHomeHeartRateMonitorSelector = createSelector(
  [(state: IAppStore) => state.homeWorkouts.userProfile?.heartRateMonitors?.home],
  (heartRateMonitorIdentifier): boolean => !!heartRateMonitorIdentifier,
);

export const preLoadPercentCompleteSelector = createSelector(
  [(state: IAppStore) => state.homeWorkouts.preloadOfExercisesDownloadProgress],
  (preloadOfExercisesDownloadProgress: number | null): number | null => (preloadOfExercisesDownloadProgress != null
    ? Math.max(1, Math.min(99, preloadOfExercisesDownloadProgress))
    : null),
);

export const shouldDisableHeartRateMonitorEditSelector = createSelector(
  [
    (state: IAppStore) => state.homeWorkouts.userProfile,
    (state: IAppStore) => state.homeWorkouts.homeWorkoutLoading,
    (state: IAppStore) => state.homeWorkouts.preloadOfExercisesLoading,
  ],
  (userProfile: UserProfile | null, homeWorkoutLoading: boolean, preloadOfExercisesLoading: boolean): boolean => {
    if (!userProfile) {
      return true;
    }
    return homeWorkoutLoading
       || (preloadOfExercisesLoading
         && !!userProfile.heartRateMonitorIdentifier
       );
  },
);

export const shouldDisableMusicEditSelector = createSelector(
  [
    (state: IAppStore) => state.music.musicInitialiseState,
    (state: IAppStore) => state.homeWorkouts.homeWorkoutLoading,
  ],
  (
    musicInitialiseState: MusicInitialiseStates,
    homeWorkoutLoading: boolean,
  ): boolean => homeWorkoutLoading || musicInitialiseState !== 'done',
);

const getHomeWorkoutSession = (state: IAppStore) => state.homeWorkouts.homeWorkoutSession;

const getCurrentSequenceInfo = (state: IAppStore) => state.homeWorkouts.currentSessionSequenceInfo;

export const percentagePreloadedSelector = createSelector(
  [getHomeWorkoutSession, getCurrentSequenceInfo],
  (homeWorkoutSession: HomeWorkoutSequence | null, sequenceInfo: ISessionSequenceInfo | null): number => {
    if (!homeWorkoutSession || !sequenceInfo) {
      return 0;
    }
    const remainingSequences = homeWorkoutSession.sequences.length - (sequenceInfo.currentIndex + 1);
    const sequencesToCheckFor = remainingSequences
    >= NUMBER_OF_SEQUENCES_TO_PRELOAD ? NUMBER_OF_SEQUENCES_TO_PRELOAD : remainingSequences;
    const percentagePerSequence = Math.ceil(100 / sequencesToCheckFor);
    let currentPercentage = 0;
    for (let i = sequenceInfo.currentIndex; i < sequenceInfo.currentIndex + sequencesToCheckFor; i++) {
      const sequence: any = homeWorkoutSession.sequences[i];
      currentPercentage += sequence.cached ? percentagePerSequence : 0;
    }
    return currentPercentage >= 100 ? 100 : currentPercentage;
  },
);

const getCurrentSequenceIndex = (state: IAppStore) => state.homeWorkouts.currentSessionSequenceInfo
  && state.homeWorkouts.currentSessionSequenceInfo.currentIndex;

const getWaitingToStartWorkout = (state: IAppStore) => {
  const { currentSessionSequenceInfo, homeWorkoutSession } = state.homeWorkouts;
  if (currentSessionSequenceInfo && homeWorkoutSession) {
    if (homeWorkoutSession.livestreamOptions?.preWorkoutTalk) {
      return currentSessionSequenceInfo.currentIndex <= 1;
    }
    return currentSessionSequenceInfo.currentIndex === 0;
  }
  return false;
};

export const inPreWorkoutTalkSelector = createSelector(
  [getHomeWorkoutSession, getCurrentSequenceInfo],
  (homeWorkoutSession, currentSequenceInfo) => (
    homeWorkoutSession?.livestreamOptions?.preWorkoutTalk && currentSequenceInfo?.currentIndex === 1
  ),
);

export const currentSequenceIndexSelector = createSelector(
  [getCurrentSequenceIndex],
  (currentIndex) => currentIndex,
);

export const waitingToStartWorkoutSelector = createSelector(
  [getWaitingToStartWorkout],
  (waitingToStart) => waitingToStart,
);

const getUserHasStartedWorkout = (state: IAppStore) => state.homeWorkouts.userHasStartedWorkout;

export const userHasStartedWorkoutdSelector = createSelector(
  [getUserHasStartedWorkout],
  (userHasStartedWorkout) => userHasStartedWorkout,
);

export const homeWorkoutSessionSelector = createSelector(
  [getHomeWorkoutSession],
  (homeWorkoutSession) => homeWorkoutSession,
);

const getUpdatedWorkoutSettings = (currentState: IAppStore) => currentState.homeWorkouts.updatedWorkoutSettings;

export const updatedWorkoutSettingsSelector = createSelector(
  [getUpdatedWorkoutSettings],
  (updatedWorkoutSettings) => JSON.parse(JSON.stringify(updatedWorkoutSettings)),
);

const getWorkoutSettingsHasChanged = (currentState: IAppStore) => !!currentState.homeWorkouts.updatedWorkoutSettings
  && !!currentState.homeWorkouts.homeWorkoutSession
  && JSON.stringify(currentState.homeWorkouts.homeWorkoutSession.workoutSettings)
  !== JSON.stringify(currentState.homeWorkouts.updatedWorkoutSettings);

export const workoutSettingsHasChangedSelector = createSelector(
  [getWorkoutSettingsHasChanged],
  (workoutSettingsHasChanged) => workoutSettingsHasChanged,
);

const getWorkoutSettingsIsUpdating = (
  currentState: IAppStore,
) => (currentState.homeWorkouts ? currentState.homeWorkouts.workoutSettingsIsUpdating : false);

export const workoutSettingsIsUpdatingSelector = createSelector(
  [getWorkoutSettingsIsUpdating],
  (workoutSettingsIsUpdating) => workoutSettingsIsUpdating,
);

const getBeepVolume = (state: IAppStore) => state.homeWorkouts.beepVolume;

export const beepVolumeSelector = createSelector(
  [getBeepVolume],
  (volume) => volume,
);

const getVoiceVolume = (state: IAppStore) => state.homeWorkouts.voiceVolume;

export const voiceVolumeSelector = createSelector(
  [getVoiceVolume],
  (volume) => volume,
);

const getDeviceSetupIsOpen = (state: IAppStore) => state.homeWorkouts.deviceSetupIsOpen;

export const deviceSetupIsOpenSelector = createSelector(
  [getDeviceSetupIsOpen],
  (deviceIsOpen) => deviceIsOpen,
);

const getOrientation = (state: IAppStore) => (state.homeWorkouts ? state.homeWorkouts.orientation : 'PORTRAIT');

export const orientationSelector = createSelector(
  [getOrientation],
  (orientation) => orientation,
);

export const isLandscapeSelector = createSelector(
  [getOrientation],
  (orientation) => orientation === Orientations.landscape,
);

const getRemainingSecondsInSequence = (state: IAppStore) => state.homeWorkouts.currentSessionSequenceInfo
  && state.homeWorkouts.currentSessionSequenceInfo.remainingSecondsInSequence;

export const remainingSecondsInSequenceSelector = createSelector(
  [getRemainingSecondsInSequence],
  (remainingSecondsInSequence) => remainingSecondsInSequence,
);

const getSetupScreen = (state: IAppStore) => state.homeWorkouts.currentSetupScreen;

export const setupScreenSelector = createSelector(
  [getSetupScreen],
  (currentSetupScreen) => currentSetupScreen,
);

const getHomeWorkoutId = (state: IAppStore) => {
  if (state.homeWorkouts.homeWorkoutSession) {
    return state.homeWorkouts.homeWorkoutSession.workoutId;
  }
};

export const homeWorkoutIdSelector = createSelector(
  [getHomeWorkoutId],
  (workoutId) => workoutId,
);

function groupCallHasNoParticipantsWithVideoEnabled(callParticipants: IGroupParticipants) {
  const participantIds = Object.keys(callParticipants);

  for (const id of participantIds) {
    const videoEnabled = !!callParticipants[id]?.agoraUid;
    if (videoEnabled) {
      return false;
    }
  }
  return true;
}

function isCurrentUserStreamingCallLeader(workout: HomeWorkoutSequence | null): boolean {
  if (!workout) {
    return false;
  }
  const userBookedWorkoutFromInvite = !!workout.pairedSessionUserProfileId;
  return !userBookedWorkoutFromInvite;
}

const getDidStreamingCallLeaderLeaveCallAfterWarmupStarted = (state: IAppStore): boolean => {
  const isStreamingWorkout = !!state.homeWorkouts?.homeWorkoutSession?.isStreamingWorkout;
  const streamerWillDropOffAfterTalk = !!state.homeWorkouts.livestreamOptions?.streamerWillDropOffAfterTalk;
  const hasWarmupOrExercisesStarted = state.homeWorkouts.warmupOrFirstExerciseHasStartedPlaying;
  if (!(isStreamingWorkout && streamerWillDropOffAfterTalk && hasWarmupOrExercisesStarted)) {
    return false;
  }
  if (isCurrentUserStreamingCallLeader(state.homeWorkouts.homeWorkoutSession)) {
    return false;
  }
  return groupCallHasNoParticipantsWithVideoEnabled(state.groupCalling.participants);
};

const shouldShowTwoLeaderColumns = (
  orientation: Orientation,
  hasSavedMetrics: boolean,
  isSavingMetrics: boolean,
  isGroupWorkout: boolean,
  mirrorIsActive: boolean,
  largeParticipantViewIsOpen: boolean,
  didStreamingCallLeaderLeaveCallAfterWarmupStarted: boolean,
) => {
  if (orientation !== Orientations.portrait || hasHomeWorkoutCompleted(hasSavedMetrics, isSavingMetrics)) {
    return false;
  }
  if ((!isGroupWorkout || didStreamingCallLeaderLeaveCallAfterWarmupStarted) && !mirrorIsActive) {
    return true;
  }
  return isGroupWorkout && largeParticipantViewIsOpen;
};

export const shouldShowTwoLeaderBoardColumnsSelector = createSelector(
  [
    (state: IAppStore) => state.homeWorkouts.orientation,
    (state: IAppStore) => state.homeWorkouts.hasSavedMetrics,
    (state: IAppStore) => state.homeWorkouts.isSavingMetrics,
    (state: IAppStore) => state.homeWorkouts.isGroupWorkout,
    (state: IAppStore) => state.homeWorkouts.mirrorIsActive,
    (state: IAppStore) => state.homeWorkouts.largeParticipantViewIsOpen,
    getDidStreamingCallLeaderLeaveCallAfterWarmupStarted,
  ],
  shouldShowTwoLeaderColumns,
);

export interface IShouldShowOnWorkout {
  shouldShowLeaderBoard: boolean;
  shouldShowTextTips: boolean;
  shouldShowBookingWorkoutSettings: boolean;
  shouldShowSmallVideoCall: boolean;
  shouldShowLargeVideoCall: boolean;
  shouldShowMirror: boolean;
  shouldShowCloseButtons: boolean;
  shouldShowWorkoutVideo: boolean;
  shouldShowTopButtons: boolean;
  shouldShowEliteInstructions: boolean;
  shouldShowEliteInstructionsDuringPause: boolean;
  shouldShowGroupWorkoutWillEndTimerInFooter: boolean;
  shouldShowWorkoutWillStartTimerInFooter: boolean;
  shouldShowGroupWorkoutWillStartTimeInFooter: boolean;
  shouldShowLeftContainerInLandscape: boolean;
  shouldShowBottomContainerInPortrait: boolean;
}

export const enableMirrorSetupSelector = createSelector(
  [
    (state: IAppStore) => state.homeWorkouts.isGroupWorkout,
    getDidStreamingCallLeaderLeaveCallAfterWarmupStarted,
  ],
  (isGroupWorkout, didStreamingCallLeaderLeaveCallAfterWarmupStarted) => {
    if (isGroupWorkout) {
      return didStreamingCallLeaderLeaveCallAfterWarmupStarted;
    }
    return true;
  },
);

export const shouldShowElementsSelector = createSelector(
  [
    (state: IAppStore) => state.homeWorkouts.hasSavedMetrics,
    (state: IAppStore) => state.homeWorkouts.isSavingMetrics,
    (state: IAppStore) => state.homeWorkouts.isGroupWorkout,
    (state: IAppStore) => state.homeWorkouts.mirrorIsActive,
    (state: IAppStore) => state.homeWorkouts.largeParticipantViewIsOpen,
    (state: IAppStore) => state.homeWorkouts.currentSessionSequenceInfo,
    (state: IAppStore) => state.homeWorkouts.leaderboardRows,
    (state: IAppStore) => state.homeWorkouts.homeWorkoutSession,
    (state: IAppStore) => state.homeWorkouts.isPastWarmdown,
    (state: IAppStore) => state.homeWorkouts.isLeaderboardActivated,
    getOrientation,
    getWaitingToStartWorkout,
    getDidStreamingCallLeaderLeaveCallAfterWarmupStarted,
  ],
  shouldShowWorkoutElements,
);

function hasHomeWorkoutCompleted(hasSavedMetrics: boolean, isSavingMetrics: boolean) {
  return hasSavedMetrics || isSavingMetrics;
}

function shouldShowWorkoutElements(
  hasSavedMetrics,
  isSavingMetrics,
  isGroupWorkout,
  mirrorIsActive,
  largeParticipantViewIsOpen,
  currentSessionSequenceInfo,
  leaderboardRows,
  homeWorkoutSession,
  isPastWarmdown,
  isLeaderboardActivated,
  orientation,
  waitingToStartWorkout,
  didStreamingCallLeaderLeaveCallAfterWarmupStarted,
): IShouldShowOnWorkout {
  const currentSequence = currentSessionSequenceInfo && currentSessionSequenceInfo.currentSequence;
  const exerciseTips = currentSequence && currentSequence.tips;

  const shouldShowEliteInstructions = !!(currentSequence
    && (currentSequence.homeBenchInstructionImageUrl || currentSequence.equipmentWeightInfo))
    && currentSequence.phase === Phase.Rest;
  const shouldShowEliteInstructionsDuringPause = !!(currentSequence
    && (currentSequence.homeBenchInstructionImageUrl || currentSequence.equipmentWeightInfo));
  const shouldShowLeaderBoard = !!(currentSequence
    && leaderboardRows != null && isLeaderboardActivated
    && !shouldShowEliteInstructions);
  const shouldShowTextTips = false;
  const shouldShowBookingWorkoutSettings = false;
  const shouldShowSmallVideoCall = !!(
    !shouldShowEliteInstructions
    && !didStreamingCallLeaderLeaveCallAfterWarmupStarted
    && currentSequence
    && isGroupWorkout
    && homeWorkoutSession
    && !largeParticipantViewIsOpen);
  const shouldShowMirror = !!(
    currentSequence
    && mirrorIsActive
    && !hasHomeWorkoutCompleted(hasSavedMetrics, isSavingMetrics)
    && (!isGroupWorkout || didStreamingCallLeaderLeaveCallAfterWarmupStarted)
    && !shouldShowEliteInstructions);
  const shouldShowCloseButtons = hasHomeWorkoutCompleted(hasSavedMetrics, isSavingMetrics);
  const shouldShowLargeVideoCall = !!(homeWorkoutSession && largeParticipantViewIsOpen)
    && !didStreamingCallLeaderLeaveCallAfterWarmupStarted;
  const shouldShowWorkoutVideo = !shouldShowLargeVideoCall;
  const shouldShowTopButtons = false;

  const isLandscape = orientation === Orientations.landscape;

  const shouldShowGroupWorkoutWillEndTimerInFooter = isGroupWorkout && isPastWarmdown && isLandscape;
  const shouldShowGroupWorkoutWillStartTimeInFooter = isGroupWorkout && waitingToStartWorkout && isLandscape;
  const shouldShowWorkoutWillStartTimerInFooter = waitingToStartWorkout && isLandscape;
  const shouldShowBottomContainerInPortrait = !shouldShowEliteInstructions
                                            && (shouldShowLeaderBoard
                                            || shouldShowMirror
                                            || shouldShowCloseButtons
                                            || shouldShowSmallVideoCall);
  const shouldShowLeftContainerInLandscape = shouldShowMirror || shouldShowLeaderBoard
                                              || shouldShowSmallVideoCall || shouldShowEliteInstructions
                                              || shouldShowCloseButtons;

  return {
    shouldShowEliteInstructions,
    shouldShowEliteInstructionsDuringPause,
    shouldShowLeaderBoard,
    shouldShowTextTips,
    shouldShowBookingWorkoutSettings,
    shouldShowSmallVideoCall,
    shouldShowMirror,
    shouldShowCloseButtons,
    shouldShowLargeVideoCall,
    shouldShowWorkoutVideo,
    shouldShowTopButtons,
    shouldShowGroupWorkoutWillEndTimerInFooter,
    shouldShowGroupWorkoutWillStartTimeInFooter,
    shouldShowWorkoutWillStartTimerInFooter,
    shouldShowBottomContainerInPortrait,
    shouldShowLeftContainerInLandscape,
  };
}

export const workoutIsReadyToStart = (state: IAppStore) => {
  const isReady = (!state.homeWorkouts.preloadOfExercisesLoading
      && !state.homeWorkouts.workoutSettingsIsUpdating)
      || state.homeWorkouts.isWorkoutFinished;
  return isReady;
};

const getWorkoutVideoError = (state: IAppStore) => state.homeWorkouts.homeWorkoutVideoError;

export const workoutVideoErrorSelector = createSelector([getWorkoutVideoError], (error) => error);

const getIsPastWarmdown = (state: IAppStore) => state.homeWorkouts.isPastWarmdown;

export const IsPastWarmdownSelector = createSelector(
  [getIsPastWarmdown],
  (isPastWarmdown) => isPastWarmdown,
);

const getInstructorName = (state: IAppStore) => {
  const instructor = state.homeWorkouts.selectedInstructor;
  if (instructor && instructor.name) {
    return instructor.name;
  }

  return '';
};

export const getInstructorNameSelector = createSelector(
  [getInstructorName],
  (instructorName) => instructorName,
);
const getIsStreamingWorkout = (state: IAppStore) => state.homeWorkouts?.homeWorkoutSession?.isStreamingWorkout;

export const classStartsInTextSelector = createSelector(
  [
    getIsStreamingWorkout,
    getWaitingToStartWorkout,
    (state: IAppStore) => state.homeWorkouts.isGroupWorkout,
    (state: IAppStore) => state.homeWorkouts.isPastWarmdown,
    (state: IAppStore) => state.homeWorkouts.inOrBeforeWarmup,
    (state: IAppStore) => !!(state.homeWorkouts.homeWorkoutId),
    getRemainingSecondsInSequence,
  ],
  (
    isStreamingWorkout,
    waitingToStartWorkout,
    isGroupWorkout,
    isPastWarmdown,
    inOrBeforeWarmup,
    openHomeWorkoutSuccess,
    remainingSecondsInSequence,
  ) => {
    if (isPastWarmdown || !openHomeWorkoutSuccess) {
      return null;
    }
    if (isStreamingWorkout) {
      if (waitingToStartWorkout && remainingSecondsInSequence) {
        return 'homeWorkouts:streamingWorkoutStartsIn';
      }
      return 'homeWorkouts:streamingWorkoutStarted';
    }
    if (isGroupWorkout) {
      if (waitingToStartWorkout && remainingSecondsInSequence) {
        return 'homeWorkouts:groupWorkoutStartsIn';
      }
      if (inOrBeforeWarmup) {
        return 'homeWorkouts:groupWarmupStarted';
      }
      return 'homeWorkouts:groupWorkoutStarted';
    }
    return null;
  },
);

export const mirrorSetupSelector = createSelector(
  [
    (state: IAppStore) => state.homeWorkouts.mirrorIsActive,
    (state: IAppStore) => state.homeWorkouts.hasCameraPermission,
  ],
  (mirrorIsActive, hasCameraPermission) => mirrorIsActive && hasCameraPermission,
);

export const selectIsStreamingWorkout = createSelector(
  [getIsStreamingWorkout],
  (isStreamingWorkout) => isStreamingWorkout,
);

export const selectIsStreamingWorkoutAndNotHost = createSelector(
  [(state: IAppStore) => state.homeWorkouts.homeWorkoutSession],
  (homeWorkoutSession) => {
    if (!homeWorkoutSession) {
      return false;
    }
    return UserWorkoutSession.isStreamingWorkoutAndNotHost(homeWorkoutSession);
  },
);

export const workoutHasPreWarmupTalkSelector = createSelector(
  [(state: IAppStore) => state.homeWorkouts.homeWorkoutSession],
  (homeWorkoutSession) => {
    if (!homeWorkoutSession) {
      return false;
    }
    return !!homeWorkoutSession.livestreamOptions?.preWorkoutTalk;
  },
);

const getDisplayMusicScreen = (state: IAppStore) => state.music?.displayMusicScreen;

export const homeWorkoutsBackgroundColorSelector = createSelector(
  [getDeviceSetupIsOpen, getDisplayMusicScreen],
  (deviceSetupIsOpen, musicScreen) => (
    musicScreen === 'MusicSpotifyOnBoarding' && deviceSetupIsOpen ? theme.WHITE : theme.BLACK
  ),
);

export const statusBarStyleSelector = createSelector(
  [getDeviceSetupIsOpen, getDisplayMusicScreen],
  (deviceSetupIsOpen, musicScreen) => (
    musicScreen === 'MusicSpotifyOnBoarding' && deviceSetupIsOpen ? 'dark-content' : 'light-content'
  ),
);

export const selectStreamingHostWorkoutId = createSelector(
  [getHomeWorkoutSession],
  (homeWorkoutSession) => homeWorkoutSession.pairedSessionUserProfileId,
);

export const exerciseFormatDescriptionSelector = createSelector(
  [getCurrentSequenceInfo, isLandscapeSelector],
  (currentSequenceInfo, isLandscape) => {
    const exerciseFormatDescription = currentSequenceInfo?.currentSequence?.exerciseFormatDescription;
    if (exerciseFormatDescription && isLandscape) {
      const [first, ...rest] = exerciseFormatDescription.toUpperCase().split(' ');
      return `${first}\n${rest.join(' ')}`;
    }
    if (exerciseFormatDescription) {
      return exerciseFormatDescription.toUpperCase();
    }
    return undefined;
  },
);
